import * as React from 'react'
import { useMediaQuery } from 'react-responsive'
import { ColorProps } from 'styled-system'
import { AppContext } from '../../contexts/ApplicationProvider.context'
import Header from '../Header'
import { Text } from '../Typography'
import HamburgerIcon from './icon'
import {
  LogoCont,
  NavItemWrapper,
  RenderWrapper,
  SideBarWrapper,
  StyledLink,
  Tooltip
} from './styles'

export type Mode = 'cover' | 'push'

type NavItems = {
  to: string
  title: string
  icon: React.ReactNode
  subMenu?: Array<{ to: string; title: string }>
}

type SideBarProps = ColorProps & {
  mode: Mode
  color: string
  hovercolor: string
  accentcolor: string
  navItems: NavItems[]
}

type SideBarLinkProps = ColorProps & {
  to: string
  open: boolean
  title: string
  color?: string
  hovercolor: string
  accentcolor: string
  icon: React.ReactNode
}

const SideBarLink: React.FC<SideBarLinkProps> = ({
  to,
  open,
  title,
  color,
  accentcolor,
  icon,
  hovercolor
}) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 40em)' })
  return (
    <StyledLink
      exact
      px={4}
      to={to}
      color={color}
      aria-label={title}
      hovercolor={hovercolor}
      accentcolor={accentcolor}
      activeClassName="active"
      className={`${open ? 'nav-link' : 'nav-link-text-hidden'}`}
    >
      <NavItemWrapper className="sidebar-nav-item-wrapper" open={open}>
        {icon}
        <Text pointer={true} ml={4}>
          {title}
        </Text>
        {!open && !isTabletOrMobile && (
          <Tooltip className="sidebar-tooltip">
            <Text color={color}>{title}</Text>
          </Tooltip>
        )}
      </NavItemWrapper>
    </StyledLink>
  )
}

const MemoSideBarLink = React.memo(SideBarLink)

const SideBar: React.FC<SideBarProps> = ({
  children,
  mode,
  navItems,
  color,
  bg,
  accentcolor,
  hovercolor
}) => {
  const { drawerOpen, toggleDrawer } = React.useContext(AppContext)

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 40em)' })

  return (
    <React.Fragment>
      <SideBarWrapper
        bg={bg}
        color={color}
        open={drawerOpen}
        mode={isTabletOrMobile ? 'cover' : mode}
      >
        <LogoCont borderBottomColor="gray.700" open={drawerOpen}>
          <HamburgerIcon open={drawerOpen} onClick={() => toggleDrawer()} />
        </LogoCont>
        {navItems.map((props) => (
          <MemoSideBarLink
            bg={bg}
            color={color}
            open={drawerOpen}
            key={props.title}
            hovercolor={hovercolor}
            accentcolor={accentcolor}
            {...props}
          />
        ))}
      </SideBarWrapper>
      <RenderWrapper
        pl={isTabletOrMobile ? 0 : mode === 'cover' ? '60px' : drawerOpen ? '250px' : '60px'}
        mode={mode}
      >
        <Header open={drawerOpen} />
        {children}
      </RenderWrapper>
    </React.Fragment>
  )
}

export default SideBar

SideBar.defaultProps = {
  mode: 'push',
  navItems: []
}
